import React, { useState } from "react";
import { Box, Button, Typography, Stack, IconButton, List, ListItem, ListItemText, Chip } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { ArtifactItem } from "../../../../../api/storage/storage";
import StorageBrowser, { StorageSelection } from "./StorageBrowser";

const RESOURCE_FILE = /\.(exe|zip|tar\.gz|tgz|whl)$/i;
const RESOURCE_URL = /^https?:\/\/.+\.(exe|zip|whl|tgz|tar\.gz)(\?.*)?$/i;

function isResourceFile(name = "") {
  return RESOURCE_FILE.test(name) || name.toLowerCase().includes("resource");
}

// Stable identity for dedupe: a storage pick is identified by bucket/path, a
// URL pick by its url.
const keyOf = (s: any) => (s?.type === "url" ? s.url : `${s?.bucket || ""}/${s?.path || ""}`);
const labelOf = (s: any) => (s?.type === "url" ? s.url : s?.name || `${s?.bucket || ""}/${s?.path || ""}`);

export default function ResourcesPicker({
  onPick,
  label = "Select resource artifacts",
}: {
  onPick: (items: any[]) => void;
  label?: string;
}) {
  const [selection, setSelection] = useState<StorageSelection>(null);
  const [items, setItems] = useState<any[]>([]);

  const addSelection = () => {
    if (!selection) return;
    const k = keyOf(selection);
    setItems((prev) => (prev.some((i) => keyOf(i) === k) ? prev : [...prev, selection]));
    setSelection(null);
  };

  const removeAt = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));

  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 0.5 }}>{label}</Typography>
      <Typography color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
        Optional — add one or more emulators or dependencies the tests need (e.g. <b>BpmEmulator.exe</b>, a wheel, or a
        zip). Pick a file, then press <b>+ Add</b>. Repeat to add more; remove any you don't need.
      </Typography>

      <StorageBrowser
        allowSelect={(item: ArtifactItem) => item.type === "file" && isResourceFile(item.name)}
        urlPattern={RESOURCE_URL}
        urlPlaceholder="https://host/bucket/resource.exe"
        urlHint="Direct resource URL (.exe/.zip/.whl/.tgz)"
        badgeFor={(item) =>
          item.type === "file" && isResourceFile(item.name) ? { label: "resource", color: "#2e7d32" } : undefined
        }
        onChange={setSelection}
      />

      <Stack direction="row" justifyContent="flex-start" sx={{ mt: 2 }}>
        <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addSelection} disabled={!selection}>
          Add
        </Button>
      </Stack>

      {items.length > 0 && (
        <Box sx={{ mt: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <List dense disablePadding>
            {items.map((it, idx) => (
              <ListItem
                key={keyOf(it) + idx}
                divider={idx < items.length - 1}
                secondaryAction={
                  <IconButton edge="end" aria-label="remove resource" onClick={() => removeAt(idx)}>
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                }
              >
                <Chip size="small" label="resource" sx={{ mr: 1.5, bgcolor: "#2e7d32", color: "#fff" }} />
                <ListItemText
                  primary={labelOf(it)}
                  primaryTypographyProps={{ sx: { fontSize: 14, wordBreak: "break-all" } }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          endIcon={<ArrowForwardRoundedIcon />}
          onClick={() => onPick(items)}
          disabled={items.length === 0}
        >
          Continue
        </Button>
      </Stack>
    </Box>
  );
}
